import React from 'react';
import { Phone, Shield, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo, certifications } from '../data/mock';

const HeroSection = () => {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-neutral-50"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-brand/10 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-brand/5 via-transparent to-transparent" />
        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      {/* Floating Elements */}
      <div className="absolute top-1/4 left-10 w-2 h-2 bg-brand rounded-full animate-pulse" />
      <div className="absolute top-1/3 right-20 w-3 h-3 bg-brand/50 rounded-full animate-bounce" style={{ animationDelay: '0.5s' }} />
      <div className="absolute bottom-1/3 left-1/4 w-2 h-2 bg-brand/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-48">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-brand/10 border border-brand/20 rounded-full px-4 py-2 mb-6">
              <Shield className="w-4 h-4 text-brand" />
              <span className="text-brand font-medium text-sm">
                {companyInfo.experience} Years of Trusted Protection
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-neutral-900 mb-6 leading-tight">
              Pest Control Specialists
              <span className="block text-brand mt-2">Across Lincolnshire</span>
            </h1>

            <p className="text-lg text-neutral-600 mb-8 max-w-xl mx-auto lg:mx-0">
              Professional pest control for homes and businesses based in Lincoln, 
              backed by {companyInfo.experience} years of experience. Bed bug specialists 
              with full coverage for all common pests. {companyInfo.tagline}.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
              <Button
                asChild
                size="lg"
                className="bg-brand hover:bg-brand-dark text-white font-bold text-lg px-8 py-6 group"
              >
                <a href="#contact">
                  Get Free Quote
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-neutral-300 text-neutral-700 hover:bg-neutral-100 hover:text-brand font-semibold text-lg px-8 py-6"
              >
                <a href={`tel:${companyInfo.phoneTel}`}>
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </a>
              </Button>
            </div>

            {/* Trust Indicators - Logos */}
            <div className="flex items-center justify-between w-full max-w-xl lg:max-w-none">
              {certifications.map((cert) => (
                <div 
                  key={cert.name} 
                  className="bg-white border border-neutral-200 rounded-lg p-2 hover:scale-105 transition-transform duration-300"
                  title={cert.fullName}
                >
                  <img 
                    src={cert.logo} 
                    alt={cert.altText}
                    className="h-10 w-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Hero Visual */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Shield Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-brand/20 to-transparent rounded-3xl blur-3xl" />
              
              {/* Stats Cards */}
              <div className="relative space-y-4">
                <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform duration-300">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-brand/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl font-bold text-brand">30+</span>
                    </div>
                    <div>
                      <p className="text-neutral-900 font-semibold">Years Experience</p>
                      <p className="text-neutral-500 text-sm">Trusted since 1995</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform duration-300">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-brand/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Shield className="w-7 h-7 text-brand" />
                    </div>
                    <div>
                      <p className="text-neutral-900 font-semibold">Bed Bug Experts</p>
                      <p className="text-neutral-500 text-sm">Specialist treatments</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform duration-300">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-brand/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-xl font-bold text-brand">24/7</span>
                    </div>
                    <div>
                      <p className="text-neutral-900 font-semibold">Emergency Response</p>
                      <p className="text-neutral-500 text-sm">Fast call-outs available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-neutral-400 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-brand rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
